import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useMemo, useState } from "react";
import {
	CartesianGrid,
	Line,
	LineChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MobileFilterField, MobileFilters } from "@/components/ui/mobile-filters";
import { PageHeader } from "@/components/ui/page-header";
import { PageNote } from "@/components/ui/page-legend";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { CennikHistoriaRow, CennikProdukt } from "@/features/contracts/contracts-api";
import { contractsApi } from "@/features/contracts/contracts-api";
import { useIsBelowMd } from "@/hooks/use-mobile";
import { getCurrentUser } from "@/lib/auth-api";
import { formatPLN } from "@/lib/format";
import { ensureAuthenticated } from "@/lib/route-guards";
import { useDebounce } from "@/lib/use-debounce";

// Backend dopuszcza tylko te role (poza nimi 403). Guard klienta odwzorowuje to
// zeby analityk/superadmin wchodzili, a reszta dostawala redirect do dashboardu.
// Nie uzywamy ensureElementVisible bo nie ma reguly widocznosci w DB dla tej
// strony (analyst nie mialby grantu -> falszywe 403).
const ALLOWED_ROLES = new Set(["super_admin", "analyst"]);

export const Route = createFileRoute("/_auth/kartoteka/cenniki-historia")({
	component: CennikiHistoriaPage,
	beforeLoad: async () => {
		await ensureAuthenticated();
		const user = getCurrentUser();
		if (!user || !user.roles.some((r) => ALLOWED_ROLES.has(r))) {
			throw redirect({ to: "/dashboard" });
		}
	},
});

/** Data w formacie YYYY-MM-DD (native <input type="date">).
 * Formatujemy z LOKALNEGO czasu — toISOString() konwertuje do UTC i tuż po
 * północy (PL = UTC+1/+2) cofnąłby domyślny zakres o dzień. */
function isoDate(d: Date): string {
	const y = d.getFullYear();
	const m = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");
	return `${y}-${m}-${day}`;
}

/** Domyslny zakres: dzis - 90 dni ... dzis. */
function defaultRange(): { od: string; do: string } {
	const today = new Date();
	const past = new Date(today);
	past.setDate(past.getDate() - 90);
	return { od: isoDate(past), do: isoDate(today) };
}

const AW_COLOR = "var(--color-chart-1)";
const AF_COLOR = "var(--color-chart-3)";

/** Krotka etykieta daty osi X (pl-PL). */
function shortDate(iso: string): string {
	const d = new Date(iso);
	return Number.isNaN(d.getTime())
		? iso
		: d.toLocaleDateString("pl-PL", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

/** Bezpieczna konwersja string->number (ceny przychodza jako string). */
function toNum(v?: string): number | null {
	if (v == null || v === "") return null;
	const n = Number(v);
	return Number.isFinite(n) ? n : null;
}

interface ChartPoint {
	validFrom: string;
	label: string;
	aw: number | null;
	af: number | null;
}

/** Scal wiersze historii w punkty wykresu: jedna data => jeden wiersz z AW+AF. */
function buildChartData(rows: CennikHistoriaRow[]): ChartPoint[] {
	const byDate = new Map<string, ChartPoint>();
	for (const r of rows) {
		const key = r.validFrom;
		const existing =
			byDate.get(key) ??
			({ validFrom: key, label: shortDate(key), aw: null, af: null } satisfies ChartPoint);
		const price = toNum(r.cenaNetto);
		if (r.segment === "baza") existing.aw = price;
		else existing.af = price;
		byDate.set(key, existing);
	}
	return [...byDate.values()].sort(
		(a, b) => new Date(a.validFrom).getTime() - new Date(b.validFrom).getTime(),
	);
}

// ── Wyszukiwarka produktu w cenniku hurtowni ─────────────────────────────────
function ProduktPicker({
	idfdost,
	selected,
	onSelect,
}: {
	idfdost: number | null;
	selected: CennikProdukt | null;
	onSelect: (p: CennikProdukt | null) => void;
}) {
	const [query, setQuery] = useState("");
	const [open, setOpen] = useState(false);
	const debounced = useDebounce(query, 300);

	const { data: results = [], isFetching } = useQuery({
		queryKey: ["cenniki", "produkty", idfdost, debounced],
		queryFn: () => contractsApi.searchCennikProdukty(idfdost as number, debounced),
		enabled: idfdost != null && open,
	});

	if (idfdost == null) {
		return <Input disabled placeholder="Najpierw wybierz hurtownię" className="bg-background" />;
	}

	return (
		<div className="relative">
			<div className="relative">
				<Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
				<Input
					value={query}
					onChange={(e) => {
						setQuery(e.target.value);
						setOpen(true);
					}}
					onFocus={() => setOpen(true)}
					onBlur={() => {
						// Opoznienie zamkniecia — pozwala klik w wynik zadzialac.
						window.setTimeout(() => setOpen(false), 150);
					}}
					placeholder="Szukaj po nazwie lub BLOZ…"
					className="pl-10 bg-background"
				/>
			</div>
			{selected && (
				<div className="mt-1.5 flex items-center gap-2 text-xs">
					<Badge className="font-mono text-[11px]">{selected.bloz07}</Badge>
					<span className="text-muted-foreground">{selected.nazwa || "—"}</span>
				</div>
			)}
			{open && (
				<div className="absolute z-20 mt-1 w-full max-h-72 overflow-auto rounded-md border border-border bg-card shadow-sm">
					{isFetching ? (
						<div className="px-3 py-2 text-xs text-muted-foreground">Szukam…</div>
					) : results.length === 0 ? (
						<div className="px-3 py-2 text-xs text-muted-foreground">Brak wyników.</div>
					) : (
						results.map((p) => (
							<button
								key={p.bloz07}
								type="button"
								onMouseDown={(e) => {
									// onMouseDown (nie onClick) — odpala sie przed blur inputa.
									e.preventDefault();
									onSelect(p);
									setQuery(p.nazwa || p.bloz07);
									setOpen(false);
								}}
								className="flex w-full flex-col items-start gap-0.5 border-b border-border px-3 py-2 text-left last:border-0 hover:bg-muted/60"
							>
								<span className="text-sm">{p.nazwa || "(bez nazwy)"}</span>
								<span className="font-mono text-[11px] text-muted-foreground">{p.bloz07}</span>
							</button>
						))
					)}
				</div>
			)}
		</div>
	);
}

/** Top 100 największych zmian ceny (ostatni krok, okno 30 dni) dla wybranej hurtowni. */
function TopZmianySekcja({ idfdost, maFranczyza }: { idfdost: number; maFranczyza: boolean }) {
	const [segment, setSegment] = useState<"baza" | "franczyza">("baza");
	const [kierunek, setKierunek] = useState<"wzrosty" | "spadki">("wzrosty");
	const { data = [], isFetching } = useQuery({
		queryKey: ["cenniki", "top-zmiany", idfdost, segment, kierunek],
		queryFn: () => contractsApi.getCennikTopZmiany(idfdost, segment, kierunek),
	});
	// Franczyza dla hurtu bez AF → dane AW (backend robi fallback); oznaczamy to jawnie.
	const fallbackAf = segment === "franczyza" && !maFranczyza;
	return (
		<Card>
			<CardContent className="px-4 py-4 space-y-3">
				<div className="flex flex-wrap items-center gap-3">
					<h3 className="text-sm font-semibold">
						Top 100 zmian cen{" "}
						<span className="font-normal text-muted-foreground">(ostatni krok, 30 dni)</span>
					</h3>
					<Select value={segment} onValueChange={(v) => setSegment(v as "baza" | "franczyza")}>
						<SelectTrigger className="h-8 w-[150px] max-md:h-9">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="baza">Baza (AW)</SelectItem>
							<SelectItem value="franczyza">
								{maFranczyza ? "Franczyza (AF)" : "Franczyza (= AW)"}
							</SelectItem>
						</SelectContent>
					</Select>
					<Select value={kierunek} onValueChange={(v) => setKierunek(v as "wzrosty" | "spadki")}>
						<SelectTrigger className="h-8 w-[130px] max-md:h-9">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="wzrosty">Wzrosty ▲</SelectItem>
							<SelectItem value="spadki">Spadki ▼</SelectItem>
						</SelectContent>
					</Select>
					<PageNote id="sort" title="Sortowanie">
						<span className="ml-auto text-[11px] text-muted-foreground">
							sort: Δ% · próg poprz. ≥ 1 zł
						</span>
					</PageNote>
				</div>
				{fallbackAf && (
					<PageNote id="segment-af" title="Segment AF">
						<div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
							Franczyza (AF) korzysta z cennika AW (baza).
						</div>
					</PageNote>
				)}
				{isFetching ? (
					<div className="py-8 text-center text-sm text-muted-foreground">Wczytywanie…</div>
				) : data.length === 0 ? (
					<div className="py-8 text-center text-sm text-muted-foreground">
						Brak zmian w ostatnich 30 dniach (pozycja potrzebuje ≥2 importów, by mieć „poprzednią"
						cenę).
					</div>
				) : (
					<div className="overflow-auto rounded-md border border-border max-h-[480px]">
						<table className="w-full text-xs border-collapse">
							<thead className="sticky top-0">
								<tr className="border-b border-border bg-muted text-left text-muted-foreground">
									<th className="px-3 py-2 font-medium">BLOZ</th>
									<th className="px-3 py-2 font-medium">Nazwa</th>
									<th className="px-3 py-2 font-medium text-right">Poprz.</th>
									<th className="px-3 py-2 font-medium text-right">Aktualna</th>
									<th className="px-3 py-2 font-medium text-right">Δ zł</th>
									<th className="px-3 py-2 font-medium text-right">Δ %</th>
									<th className="px-3 py-2 font-medium">Data</th>
								</tr>
							</thead>
							<tbody>
								{data.map((r) => {
									const pct = Number(r.deltaPct);
									const up = pct > 0;
									const cls = up ? "text-status-error-text" : "text-status-success-text";
									return (
										<tr
											key={r.bloz07}
											className="border-b border-border last:border-0 hover:bg-muted/50"
										>
											<td className="px-3 py-1.5 font-mono tabular-nums">{r.bloz07}</td>
											<td className="px-3 py-1.5">{r.nazwa ?? "—"}</td>
											<td className="px-3 py-1.5 text-right font-mono tabular-nums">
												{formatPLN(Number(r.cenaPoprzednia))}
											</td>
											<td className="px-3 py-1.5 text-right font-mono tabular-nums">
												{formatPLN(Number(r.cenaAktualna))}
											</td>
											<td className={`px-3 py-1.5 text-right font-mono tabular-nums ${cls}`}>
												{up ? "+" : ""}
												{formatPLN(Number(r.deltaZl))}
											</td>
											<td
												className={`px-3 py-1.5 text-right font-mono tabular-nums font-medium ${cls}`}
											>
												{up ? "+" : ""}
												{pct.toFixed(2)}%
											</td>
											<td className="px-3 py-1.5 tabular-nums">{shortDate(r.dataZmiany)}</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</CardContent>
		</Card>
	);
}

function CennikiHistoriaPage() {
	const [idfdost, setIdfdost] = useState<number | null>(null);
	const [produkt, setProdukt] = useState<CennikProdukt | null>(null);
	const range = useMemo(() => defaultRange(), []);
	const [od, setOd] = useState(range.od);
	const [doDate, setDoDate] = useState(range.do);
	// Przełącznik widoku: wykres jednego produktu vs top 100 zmian całej hurtowni.
	const [view, setView] = useState<"wykres" | "top">("wykres");

	// Lista hurtowni ze skonfigurowanym źródłem cennika (endpoint dostępny dla
	// super_admin + analyst — poprzedni getAllContractorSources był super_admin-only
	// i analityk dostawał 403 -> pusty dropdown).
	const { data: hurtownie = [] } = useQuery({
		queryKey: ["cenniki", "hurtownie"],
		queryFn: contractsApi.getCennikHurtownie,
	});

	const wholesalers = useMemo(
		() =>
			[...hurtownie].sort((a, b) =>
				(a.nazwa || `#${a.idfdost}`).localeCompare(b.nazwa || `#${b.idfdost}`, "pl"),
			),
		[hurtownie],
	);

	const selectedHurt = wholesalers.find((w) => w.idfdost === idfdost);

	const canQuery = idfdost != null && produkt != null && od !== "" && doDate !== "";

	const {
		data: historia = [],
		isFetching,
		isLoading,
	} = useQuery({
		queryKey: ["cenniki", "historia", idfdost, produkt?.bloz07, od, doDate],
		queryFn: () =>
			contractsApi.getCennikHistoria({
				idfdost: idfdost as number,
				bloz07: produkt?.bloz07 as string,
				od,
				do: doDate,
			}),
		enabled: canQuery,
	});

	const chartData = useMemo(() => buildChartData(historia), [historia]);
	const hasAf = useMemo(() => historia.some((r) => r.segment === "franczyza"), [historia]);

	// Telefon: hurtownia i okres w chipach/arkuszu filtrow, wyszukiwarka produktu
	// zostaje widoczna nad wykresem (strona zaczyna sie od wyboru produktu).
	const isBelowMd = useIsBelowMd();
	const hurtowniaSelect = (
		<Select
			value={idfdost != null ? String(idfdost) : undefined}
			onValueChange={(v) => {
				setIdfdost(Number(v));
				setProdukt(null);
			}}
		>
			<SelectTrigger className="h-10 w-full">
				<SelectValue placeholder="Wybierz hurtownię" />
			</SelectTrigger>
			<SelectContent>
				{wholesalers.map((w) => (
					<SelectItem key={w.idfdost} value={String(w.idfdost)}>
						{w.nazwa || `#${w.idfdost}`}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);
	const okresFields = (
		<div className="grid grid-cols-2 gap-2">
			<MobileFilterField label="Od">
				<Input
					type="date"
					value={od}
					max={doDate}
					onChange={(e) => setOd(e.target.value)}
					className="h-10 w-full bg-background text-sm"
				/>
			</MobileFilterField>
			<MobileFilterField label="Do">
				<Input
					type="date"
					value={doDate}
					min={od}
					onChange={(e) => setDoDate(e.target.value)}
					className="h-10 w-full bg-background text-sm"
				/>
			</MobileFilterField>
		</div>
	);
	const hurtowniaLabel = selectedHurt
		? selectedHurt.nazwa || `#${selectedHurt.idfdost}`
		: "Wybierz hurtownię";

	return (
		<div className="flex flex-col h-full overflow-hidden">
			<div className="p-6 pb-2 max-md:px-3 max-md:pt-3 max-md:pb-1 max-md:empty:hidden">
				<PageHeader
					title="Historia cen (hurt)"
					description="Zmiany cen netto w cennikach hurtowych — segmenty AW (baza) vs AF (franczyza)"
				/>
			</div>

			<MobileFilters
				chips={[
					{
						id: "hurtownia",
						label: "Hurtownia",
						value: hurtowniaLabel,
						active: idfdost != null,
						picker: hurtowniaSelect,
					},
					...(view === "wykres"
						? [
								{
									id: "okres",
									label: "Okres",
									value: `${shortDate(od)}–${shortDate(doDate)}`,
									active: od !== range.od || doDate !== range.do,
									picker: okresFields,
								},
							]
						: []),
				]}
				sheetContent={
					<>
						<MobileFilterField label="Hurtownia">{hurtowniaSelect}</MobileFilterField>
						{view === "wykres" && okresFields}
					</>
				}
				onClear={() => {
					setOd(range.od);
					setDoDate(range.do);
				}}
				resultLabel={view === "wykres" ? "Pokaż historię" : "Pokaż zmiany"}
			>
				{/* Toolbar kontrolek */}
				<div className="px-6 py-3 border-y bg-card/50 grid grid-cols-1 gap-4 md:grid-cols-4">
					<div className="flex flex-col gap-1.5">
						<Label>Hurtownia</Label>
						<Select
							value={idfdost != null ? String(idfdost) : undefined}
							onValueChange={(v) => {
								setIdfdost(Number(v));
								setProdukt(null);
							}}
						>
							<SelectTrigger className="w-full">
								<SelectValue placeholder="Wybierz hurtownię" />
							</SelectTrigger>
							<SelectContent>
								{wholesalers.map((w) => (
									<SelectItem key={w.idfdost} value={String(w.idfdost)}>
										{w.nazwa || `#${w.idfdost}`}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					{view === "wykres" && (
						<>
							<div className="flex flex-col gap-1.5">
								<Label>Produkt</Label>
								<ProduktPicker
									key={idfdost ?? "none"}
									idfdost={idfdost}
									selected={produkt}
									onSelect={setProdukt}
								/>
							</div>

							<div className="flex flex-col gap-1.5">
								<Label htmlFor="cennik-od">Od</Label>
								<Input
									id="cennik-od"
									type="date"
									value={od}
									max={doDate}
									onChange={(e) => setOd(e.target.value)}
									className="bg-background"
								/>
							</div>

							<div className="flex flex-col gap-1.5">
								<Label htmlFor="cennik-do">Do</Label>
								<Input
									id="cennik-do"
									type="date"
									value={doDate}
									min={od}
									onChange={(e) => setDoDate(e.target.value)}
									className="bg-background"
								/>
							</div>
						</>
					)}
				</div>
			</MobileFilters>
			{isBelowMd && view === "wykres" && (
				<div className="px-3 pt-2">
					<ProduktPicker
						key={idfdost ?? "none"}
						idfdost={idfdost}
						selected={produkt}
						onSelect={setProdukt}
					/>
				</div>
			)}

			{/* Przełącznik widoku */}
			<div className="px-6 pt-3 max-md:px-3 max-md:pt-2">
				<Tabs value={view} onValueChange={(v) => setView(v as "wykres" | "top")}>
					<TabsList className="max-md:w-full">
						<TabsTrigger value="wykres">Historia produktu</TabsTrigger>
						<TabsTrigger value="top">Top 100 zmian</TabsTrigger>
					</TabsList>
				</Tabs>
			</div>

			<div className="flex-1 min-h-0 overflow-auto p-6 pt-4 space-y-4 max-md:p-3 max-md:pt-3 max-md:space-y-3">
				{view === "wykres" ? (
					!canQuery ? (
						<Card>
							<CardContent className="py-16 text-center text-sm text-muted-foreground">
								Wybierz hurtownię i produkt.
							</CardContent>
						</Card>
					) : isLoading || isFetching ? (
						<Card>
							<CardContent className="py-16 text-center text-sm text-muted-foreground">
								Wczytywanie…
							</CardContent>
						</Card>
					) : historia.length === 0 ? (
						<Card>
							<CardContent className="py-16 text-center text-sm text-muted-foreground">
								Brak historii w wybranym okresie.
							</CardContent>
						</Card>
					) : (
						<>
							<Card>
								<CardContent className="pt-6">
									<div style={{ width: "100%", height: 320, minWidth: 0 }}>
										<ResponsiveContainer width="100%" height={320} debounce={50}>
											<LineChart
												data={chartData}
												margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
											>
												<CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
												<XAxis dataKey="label" tick={{ fontSize: 11 }} />
												<YAxis
													tick={{ fontSize: 11 }}
													width={56}
													domain={["auto", "auto"]}
													tickFormatter={(v) => formatPLN(Number(v))}
												/>
												<Tooltip
													formatter={(value, name) => [
														`${formatPLN(Number(value))} zł`,
														String(name),
													]}
													labelFormatter={(label) => `Data: ${String(label)}`}
													contentStyle={{
														backgroundColor: "var(--background)",
														border: "1px solid var(--border)",
														borderRadius: 6,
														fontSize: 12,
													}}
												/>
												<Line
													name="AW"
													type="monotone"
													dataKey="aw"
													stroke={AW_COLOR}
													strokeWidth={2}
													dot={{ r: 3, fill: AW_COLOR }}
													activeDot={{ r: 5 }}
													connectNulls
													isAnimationActive={false}
												/>
												{hasAf && (
													<Line
														name="AF"
														type="monotone"
														dataKey="af"
														stroke={AF_COLOR}
														strokeWidth={2}
														strokeDasharray="4 4"
														dot={{ r: 3, fill: AF_COLOR }}
														activeDot={{ r: 5 }}
														connectNulls
														isAnimationActive={false}
													/>
												)}
											</LineChart>
										</ResponsiveContainer>
									</div>

									{/* Statyczna legenda — bez recharts <Legend> (petla #185 na Safari). */}
									<div className="flex items-center justify-center gap-4 mt-2 text-[11px] text-muted-foreground">
										<span className="flex items-center gap-1.5">
											<span
												className="inline-block h-0.5 w-4 rounded"
												style={{ backgroundColor: AW_COLOR }}
											/>
											AW (baza)
										</span>
										{hasAf && (
											<span className="flex items-center gap-1.5">
												<span
													className="inline-block h-0 w-4 border-t-2 border-dashed"
													style={{ borderColor: AF_COLOR }}
												/>
												AF (franczyza)
											</span>
										)}
									</div>
								</CardContent>
							</Card>

							{/* Surowe punkty — pomaga gdy jest tylko 1 snapshot. */}
							<Card>
								<CardContent className="px-4 py-4">
									<div className="overflow-auto rounded-md border border-border">
										<table className="w-full text-xs border-collapse">
											<thead>
												<tr className="border-b border-border bg-muted/50 text-left text-muted-foreground">
													<th className="px-3 py-2 font-medium">Segment</th>
													<th className="px-3 py-2 font-medium">Data</th>
													<th className="px-3 py-2 font-medium text-right">Cena netto</th>
												</tr>
											</thead>
											<tbody>
												{[...historia]
													.sort(
														(a, b) =>
															new Date(a.validFrom).getTime() - new Date(b.validFrom).getTime(),
													)
													.map((r, i) => (
														<tr
															// biome-ignore lint/suspicious/noArrayIndexKey: historia tylko do odczytu; segment i data moga sie powtorzyc.
															key={`${r.segment}-${r.validFrom}-${i}`}
															className="border-b border-border last:border-0 hover:bg-muted/50"
														>
															<td className="px-3 py-2">
																<Badge className="uppercase text-[11px]">
																	{r.segment === "baza" ? "AW" : "AF"}
																</Badge>
															</td>
															<td className="px-3 py-2 tabular-nums">{shortDate(r.validFrom)}</td>
															<td className="px-3 py-2 text-right font-mono tabular-nums">
																{toNum(r.cenaNetto) != null
																	? formatPLN(toNum(r.cenaNetto) as number)
																	: "—"}
															</td>
														</tr>
													))}
											</tbody>
										</table>
									</div>
								</CardContent>
							</Card>
						</>
					)
				) : idfdost != null ? (
					<TopZmianySekcja idfdost={idfdost} maFranczyza={selectedHurt?.maFranczyza ?? false} />
				) : (
					<Card>
						<CardContent className="py-16 text-center text-sm text-muted-foreground">
							Wybierz hurtownię, aby zobaczyć top zmian cen.
						</CardContent>
					</Card>
				)}
			</div>
		</div>
	);
}
